import React, { useState, useEffect, useReducer, useCallback, useContext, useRef } from "react";
import openSocket from "../../services/socket-io";
import { isSameDay, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const NO_QUEUE_FILTER_ID = -1;

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		backgroundColor:
			theme.palette.type === "dark" ? "rgba(11,23,20,0.55)" : "#eceaf3",
		borderTop: "none",
		paddingTop: 2,
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
	ticketItemWrapper: {
		opacity: 1,
		transform: "translateY(0) scale(1)",
		transition: "opacity 180ms ease, transform 180ms ease, background-color 220ms ease",
	},
	ticketItemEntering: {
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(20, 184, 166, 0.08)"
				: "rgba(20, 184, 166, 0.12)",
	},
	ticketItemRemoving: {
		opacity: 0,
		transform: "translateY(-4px) scale(0.99)",
		pointerEvents: "none",
	},
}));

const reducer = (state, action) => {
	const areTicketsEquivalent = (previousTicket, nextTicket) => {
		if (previousTicket === nextTicket) return true;
		if (!previousTicket || !nextTicket) return false;

		const previousContact = previousTicket.contact || {};
		const nextContact = nextTicket.contact || {};
		const previousQueue = previousTicket.queue || {};
		const nextQueue = nextTicket.queue || {};
		const previousUser = previousTicket.user || {};
		const nextUser = nextTicket.user || {};

		return (
			previousTicket.id === nextTicket.id &&
			previousTicket.status === nextTicket.status &&
			previousTicket.unreadMessages === nextTicket.unreadMessages &&
			previousTicket.updatedAt === nextTicket.updatedAt &&
			previousTicket.lastMessage === nextTicket.lastMessage &&
			previousTicket.lastMessageType === nextTicket.lastMessageType &&
			previousTicket.lastMessageMediaType === nextTicket.lastMessageMediaType &&
			previousTicket.mediaType === nextTicket.mediaType &&
			previousTicket.isGroup === nextTicket.isGroup &&
			previousTicket.userId === nextTicket.userId &&
			previousTicket.userName === nextTicket.userName &&
			previousTicket.queueId === nextTicket.queueId &&
			previousTicket.contactId === nextTicket.contactId &&
			previousContact.id === nextContact.id &&
			previousContact.name === nextContact.name &&
			previousContact.profilePicUrl === nextContact.profilePicUrl &&
			previousQueue.id === nextQueue.id &&
			previousQueue.name === nextQueue.name &&
			previousQueue.color === nextQueue.color &&
			previousUser.id === nextUser.id &&
			previousUser.name === nextUser.name
		);
	};

	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = areTicketsEquivalent(state[ticketIndex], ticket)
					? state[ticketIndex]
					: ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "REPLACE_TICKETS") {
		const newTickets = Array.isArray(action.payload) ? action.payload : [];
		const uniqueTickets = [];
		const seenTicketIds = new Set();
		const previousTicketsById = new Map(state.map(ticket => [ticket.id, ticket]));

		newTickets.forEach(ticket => {
			if (!ticket?.id || seenTicketIds.has(ticket.id)) return;
			seenTicketIds.add(ticket.id);
			const previousTicket = previousTicketsById.get(ticket.id);
			uniqueTickets.push(
				previousTicket && areTicketsEquivalent(previousTicket, ticket)
					? previousTicket
					: ticket
			);
		});

		return uniqueTickets;
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			const previousTicket = state[ticketIndex];
			const previousUserMatches =
				previousTicket.user &&
				Number(previousTicket.user.id || 0) === Number(ticket.userId || previousTicket.userId || 0);
			state[ticketIndex] = {
				...previousTicket,
				...ticket,
				user: ticket.user || (previousUserMatches ? previousTicket.user : null)
			};
		} else if (action.allowInsert !== false) {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			const previousTicket = state[ticketIndex];
			const previousUserMatches =
				previousTicket.user &&
				Number(previousTicket.user.id || 0) === Number(ticket.userId || previousTicket.userId || 0);
			state[ticketIndex] = {
				...previousTicket,
				...ticket,
				user: ticket.user || (previousUserMatches ? previousTicket.user : null)
			};
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else if (action.allowInsert !== false) {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

	const TicketsList = (props) => {
		const {
			status,
			searchParam,
			showAll,
			selectedQueueIds,
			requestQueueIds,
			selectedUserIds,
			selectedWhatsAppIds,
			selectedTagIds,
			selectedDate,
			selectedTicketId,
			ticketType,
			updateCount,
			refreshTrigger,
			style
		} =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const [removingTicketIds, setRemovingTicketIds] = useState({});
	const [recentlyChangedTicketIds, setRecentlyChangedTicketIds] = useState({});
	const removalTimeoutsRef = useRef({});
	const highlightTimeoutsRef = useRef({});
	const markTicketAsChanged = useCallback(ticketId => {
		if (!ticketId) return;
		setRecentlyChangedTicketIds(prevState => ({
			...prevState,
			[ticketId]: true
		}));

		if (highlightTimeoutsRef.current[ticketId]) {
			clearTimeout(highlightTimeoutsRef.current[ticketId]);
		}

		highlightTimeoutsRef.current[ticketId] = setTimeout(() => {
			setRecentlyChangedTicketIds(prevState => {
				const nextState = { ...prevState };
				delete nextState[ticketId];
				return nextState;
			});
			delete highlightTimeoutsRef.current[ticketId];
		}, 750);
	}, []);

	const removeTicketWithAnimation = useCallback(ticketId => {
		if (!ticketId) return;
		setRemovingTicketIds(prevState => ({
			...prevState,
			[ticketId]: true
		}));

		if (removalTimeoutsRef.current[ticketId]) {
			clearTimeout(removalTimeoutsRef.current[ticketId]);
		}

		removalTimeoutsRef.current[ticketId] = setTimeout(() => {
			dispatch({ type: "DELETE_TICKET", payload: ticketId });
			setRemovingTicketIds(prevState => {
				const nextState = { ...prevState };
				delete nextState[ticketId];
				return nextState;
			});
			delete removalTimeoutsRef.current[ticketId];
		}, 190);
	}, []);

	useEffect(() => {
		return () => {
			Object.values(removalTimeoutsRef.current).forEach(timeoutId =>
				clearTimeout(timeoutId)
			);
			Object.values(highlightTimeoutsRef.current).forEach(timeoutId =>
				clearTimeout(timeoutId)
			);
		};
	}, []);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		setRemovingTicketIds({});
		setRecentlyChangedTicketIds({});
	}, [
		status,
		searchParam,
		dispatch,
		showAll,
		requestQueueIds,
		selectedQueueIds,
		selectedUserIds,
		selectedWhatsAppIds,
		selectedTagIds,
		selectedDate,
		selectedTicketId,
		ticketType
	]);

	const { user } = useContext(AuthContext);

	// Com "Todos" desligado a listagem HTTP ja nao traz ticket de outro atendente,
	// mas o evento de socket chega assim mesmo (admin esta entre os destinatarios
	// de todo ticket da empresa). Sem esta guarda o ticket alheio era inserido na
	// lista assim que recebia mensagem.
	// So bloqueia INSERCAO: ticket ja presente continua atualizando, senao a fila
	// compartilhada (onde a listagem legitimamente traz ticket de outro) perderia
	// o tempo real e sumiria da tela.
	const canInsertTicket = useCallback(
		ticket => {
			if (showAll) return true;
			const owner = Number(ticket?.userId || 0);
			if (!owner) return true;
			return owner === Number(user?.id || 0);
		},
		[showAll, user]
	);

	const shouldIncludeTicket = useCallback(
		ticket => {
			const queueFilterIds = Array.isArray(requestQueueIds)
				? requestQueueIds
				: selectedQueueIds;
			const hasQueueFilter = queueFilterIds.length > 0;
			const hasUserFilter = (selectedUserIds || []).length > 0;
			const hasWhatsAppFilter = (selectedWhatsAppIds || []).length > 0;
			const hasTagFilter = (selectedTagIds || []).length > 0;
			const hasDateFilter = Boolean(selectedDate);
			// Filtro por id e exato: com ele ligado a lista tem no maximo uma
			// linha, entao qualquer outro ticket que chegue por socket nao entra.
			const ticketIdFilter = Number(selectedTicketId || 0);

			const matchesCurrentStatus = !status || ticket.status === status;
			const matchesAssigneeFilter =
				!hasUserFilter ||
				(selectedUserIds || []).indexOf(Number(ticket.userId)) > -1;
			const matchesWhatsAppFilter =
				!hasWhatsAppFilter ||
				(selectedWhatsAppIds || []).indexOf(Number(ticket.whatsappId)) > -1;
			const matchesTagFilter = !hasTagFilter
				? true
				: (selectedTagIds || []).some(tagId =>
						(Array.isArray(ticket?.tags) ? ticket.tags : [])
							.map(tag => Number(tag.id))
							.indexOf(Number(tagId)) > -1
				  );
			const matchesDateFilter = !hasDateFilter || !ticket?.createdAt
				? true
				: (() => {
						try {
							return isSameDay(parseISO(ticket.createdAt), parseISO(selectedDate));
						} catch (error) {
							return true;
						}
				  })();
			const matchesTicketIdFilter =
				!ticketIdFilter || Number(ticket?.id || 0) === ticketIdFilter;
			const matchesQueueFilter = !hasQueueFilter
				? true
				: ticket.queueId
					? queueFilterIds.indexOf(Number(ticket.queueId)) > -1
					: queueFilterIds.indexOf(NO_QUEUE_FILTER_ID) > -1;

			return (
				!searchParam &&
				matchesCurrentStatus &&
				matchesAssigneeFilter &&
				matchesWhatsAppFilter &&
				matchesTagFilter &&
				matchesDateFilter &&
				matchesTicketIdFilter &&
				matchesQueueFilter &&
				(ticketType === "all" ||
					(ticketType === "group" && ticket.isGroup) ||
					(ticketType === "private" && !ticket.isGroup))
			);
		},
		[
			searchParam,
			selectedDate,
			requestQueueIds,
			selectedQueueIds,
			selectedTagIds,
			selectedUserIds,
			selectedWhatsAppIds,
			selectedTicketId,
			status,
			ticketType
		]
	);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		ticketType,
		queueIds: JSON.stringify(requestQueueIds || selectedQueueIds),
		userIds: JSON.stringify(selectedUserIds || []),
		whatsappIds: JSON.stringify(selectedWhatsAppIds || []),
		tagIds: JSON.stringify(selectedTagIds || []),
		date: selectedDate,
		ticketId: selectedTicketId,
		refreshTrigger
	});

	useEffect(() => {
		// Busca por id nao tem status: o id ja identifica um chamado so, e exigir
		// status aqui deixaria a lista vazia para sempre.
		if (!status && !searchParam && !selectedTicketId) return;
		dispatch({
			type: pageNumber === 1 ? "REPLACE_TICKETS" : "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets, pageNumber, status, searchParam, selectedTicketId]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		socket.on("ticket", data => {
			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			if (data.action === "update") {
				if (shouldIncludeTicket(data.ticket)) {
					if (
						data.meta?.source === "message" &&
						Number(data.ticket?.unreadMessages || 0) > 0
					) {
						dispatch({
							type: "UPDATE_TICKET_UNREAD_MESSAGES",
							payload: data.ticket,
							allowInsert: canInsertTicket(data.ticket),
						});
						markTicketAsChanged(data.ticket.id);
						return;
					}

					dispatch({
						type: "UPDATE_TICKET",
						payload: data.ticket,
						allowInsert: canInsertTicket(data.ticket),
					});
					markTicketAsChanged(data.ticket.id);
					return;
				}

				removeTicketWithAnimation(data.ticket.id);
			}

			if (data.action === "delete") {
				removeTicketWithAnimation(data.ticketId);
			}
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [
		status,
		shouldIncludeTicket,
		canInsertTicket,
		markTicketAsChanged,
		removeTicketWithAnimation
	]);

	useEffect(() => {
		const handleAcceptedTicket = event => {
			const acceptedTicket = event?.detail?.ticket;
			if (!acceptedTicket?.id) return;

			if (status === "pending") {
				removeTicketWithAnimation(acceptedTicket.id);
				return;
			}

			if (status !== "open") return;

			if (ticketType === "group" && !acceptedTicket.isGroup) return;
			if (ticketType === "private" && acceptedTicket.isGroup) return;

			dispatch({
				type: "UPDATE_TICKET",
				payload: acceptedTicket,
			});
			markTicketAsChanged(acceptedTicket.id);
		};

		window.addEventListener("ticket:accepted", handleAcceptedTicket);
		return () => {
			window.removeEventListener("ticket:accepted", handleAcceptedTicket);
		};
	}, [status, ticketType, markTicketAsChanged, removeTicketWithAnimation]);

	useEffect(() => {
		const handleUpdatedTicket = event => {
			const updatedTicket = event?.detail?.ticket;
			if (!updatedTicket?.id) return;

			if (shouldIncludeTicket(updatedTicket)) {
				dispatch({
					type: "UPDATE_TICKET",
					payload: updatedTicket,
				});
				markTicketAsChanged(updatedTicket.id);
				return;
			}

			removeTicketWithAnimation(updatedTicket.id);
		};

		window.addEventListener("ticket:updated", handleUpdatedTicket);
		return () => {
			window.removeEventListener("ticket:updated", handleUpdatedTicket);
		};
	}, [
		shouldIncludeTicket,
		markTicketAsChanged,
		removeTicketWithAnimation
	]);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{ticketsList.map(ticket => (
								<div
									key={ticket.id}
									className={`${classes.ticketItemWrapper} ${
										recentlyChangedTicketIds[ticket.id]
											? classes.ticketItemEntering
											: ""
									} ${
										removingTicketIds[ticket.id]
											? classes.ticketItemRemoving
											: ""
									}`}
								>
									<TicketListItem ticket={ticket} />
								</div>
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
